"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

interface CarouselProps {
    /** Section heading shown to the left of the arrows. */
    title: string;
    /** Optional icon rendered before the title (e.g. a <TrendingUp />). */
    icon?: React.ReactNode;
    /** The cards — rendered by the parent (often a server component). */
    children: React.ReactNode;
}

/** kebab id from a title so the arrows have stable, per-section test ids. */
function slugify(title: string): string {
    return title
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
}

/**
 * A horizontally scrollable section with a heading and working prev/next
 * arrows. The arrows scroll the track by ~90% of the viewport and disable
 * themselves at each end; native swipe/trackpad scrolling still works. Cards
 * are passed as `children`, so each surface (browse, discovery, …) renders its
 * own card markup while sharing one scroll + control implementation.
 */
export function Carousel({ title, icon, children }: CarouselProps) {
    const trackRef = useRef<HTMLDivElement>(null);
    const [atStart, setAtStart] = useState(true);
    const [atEnd, setAtEnd] = useState(false);

    const updateEdges = useCallback(() => {
        const el = trackRef.current;
        if (!el) return;
        // 1px tolerance absorbs sub-pixel rounding at the extremes.
        setAtStart(el.scrollLeft <= 1);
        setAtEnd(el.scrollLeft + el.clientWidth >= el.scrollWidth - 1);
    }, []);

    useEffect(() => {
        const el = trackRef.current;
        if (!el) return;
        // Measure after first paint (avoids a synchronous setState in the
        // effect body) and keep in sync with scroll + resize.
        const raf = requestAnimationFrame(updateEdges);
        el.addEventListener("scroll", updateEdges, { passive: true });
        window.addEventListener("resize", updateEdges);
        return () => {
            cancelAnimationFrame(raf);
            el.removeEventListener("scroll", updateEdges);
            window.removeEventListener("resize", updateEdges);
        };
    }, [updateEdges]);

    const scrollByPage = (direction: -1 | 1) => {
        const el = trackRef.current;
        if (!el) return;
        // Advance by ~90% of the viewport so a card stays partly visible as an
        // affordance that there's more.
        el.scrollBy({ left: direction * el.clientWidth * 0.9, behavior: "smooth" });
    };

    const slug = slugify(title);
    const arrowClass =
        "flex size-11 md:size-8 items-center justify-center rounded-full border border-slate-200 text-slate-400 transition-all enabled:hover:border-primary enabled:hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed dark:border-primary/20";

    return (
        <section>
            <div className="mb-4 flex items-center justify-between">
                <h2 className="flex items-center gap-2 font-heading text-xl font-bold text-slate-900 dark:text-white md:text-2xl">
                    {icon}
                    {title}
                </h2>
                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={() => scrollByPage(-1)}
                        disabled={atStart}
                        aria-label={`Scroll ${title} left`}
                        data-testid={`carousel-prev-${slug}`}
                        className={arrowClass}
                    >
                        <ChevronLeft className="size-5" />
                    </button>
                    <button
                        type="button"
                        onClick={() => scrollByPage(1)}
                        disabled={atEnd}
                        aria-label={`Scroll ${title} right`}
                        data-testid={`carousel-next-${slug}`}
                        className={arrowClass}
                    >
                        <ChevronRight className="size-5" />
                    </button>
                </div>
            </div>

            <div
                ref={trackRef}
                className="custom-scrollbar flex snap-x scroll-smooth gap-4 overflow-x-auto pb-4"
            >
                {children}
            </div>
        </section>
    );
}
